import React, { memo, useEffect, useRef } from 'react';
import { ActionButton } from '@adobe/react-spectrum';
import classNames from 'classnames';
import SavedViewMoreIcon from 'app/assets/svgIcons/SavedViewMoreIcon';
import styles from './SavedViewContextMenu.module.scss';

interface SavedViewContextMenuProps {
  savedViewId: string;
  savedViewName: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const SavedViewContextMenu = memo(
  ({
    savedViewId,
    savedViewName,
    isOpen,
    onOpenChange,
    onEdit,
    onDelete
  }: SavedViewContextMenuProps) => {
    const menuContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuContainerRef.current &&
          !menuContainerRef.current.contains(event.target as Node)
        ) {
          onOpenChange(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen, onOpenChange]);

    const handleEditClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      onEdit();
      onOpenChange(false);
    };

    const handleDeleteClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      onDelete();
      onOpenChange(false);
    };

    return (
      <div
        ref={menuContainerRef}
        className={classNames(
          styles.menuContainer,
          isOpen && styles.menuContainerOpen
        )}
      >
        <ActionButton
          isQuiet
          UNSAFE_className={classNames(
            styles.moreButton,
            'saved-view-more-button',
            isOpen && styles.moreButtonVisible
          )}
          aria-label={`More actions for ${savedViewName}`}
          aria-expanded={isOpen}
          aria-haspopup="true"
          data-testid={`saved-view-more-${savedViewId}`}
          onPress={() => onOpenChange(!isOpen)}
        >
          <SavedViewMoreIcon width={16} height={16} />
        </ActionButton>

        {isOpen && (
          <div
            className={styles.dropdownMenu}
            role="menu"
            data-testid={`saved-view-menu-${savedViewId}`}
          >
            <button
              type="button"
              className={styles.menuItem}
              role="menuitem"
              onClick={handleEditClick}
              data-testid={`saved-view-edit-${savedViewId}`}
            >
              <span className={styles.menuItemText}>Edit View</span>
            </button>
            <button
              type="button"
              className={styles.menuItem}
              role="menuitem"
              onClick={handleDeleteClick}
              data-testid={`saved-view-delete-${savedViewId}`}
            >
              <span className={styles.menuItemText}>Delete View</span>
            </button>
          </div>
        )}
      </div>
    );
  }
);

SavedViewContextMenu.displayName = 'SavedViewContextMenu';
